import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { Link, useOutletContext, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    IoArrowForwardOutline,
    IoCalendarOutline,
    IoCashOutline,
    IoDocumentTextOutline,
    IoFlaskOutline,
    IoGridOutline,
    IoPencilOutline,
    IoPulseOutline,
    IoSparklesOutline,
    IoTimeOutline,
} from "react-icons/io5";

import GetData from "../../services/getData";
import { getLocale } from "../../i18n/config";
import PatientPayments from "./tabs/PatientPayments";
import PatientHistory from "./tabs/PatientHistory";
import PatientAiOrb from "./PatientAiOrb";
import { Button } from "../../components/ui";
import "./patientOverview.css";

/* -------------------------------------------------------------------------- */
/*  Локальные помощники отображения                                           */
/* -------------------------------------------------------------------------- */

const displayValue = (value) => {
    if (value === 0) return "0";
    const text = String(value ?? "").trim();
    if (!text || text === "0000-00-00" || text === "0000-00-00 00:00:00") {
        return "—";
    }
    return text;
};

const GENDER_LABEL_KEYS = {
    Мужской: "layout.gender.male",
    Женский: "layout.gender.female",
};

const displayGender = (value, t) => {
    const key = GENDER_LABEL_KEYS[String(value ?? "").trim()];
    return key ? t(key) : displayValue(value);
};

const formatBirthDate = (isoDate, locale, t) => {
    const raw = String(isoDate ?? "").trim();
    if (!raw || raw.startsWith("0000-00-00")) return "—";
    const [datePart] = raw.split(/[ T]/);
    const [year, month, day] = datePart.split("-");
    if (!year || !month || !day) return "—";

    const birth = new Date(Number(year), Number(month) - 1, Number(day));
    if (Number.isNaN(birth.getTime())) return `${day}.${month}.${year}`;

    const now = new Date();
    let age = now.getFullYear() - birth.getFullYear();
    const monthDiff = now.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < birth.getDate())) {
        age -= 1;
    }

    const formatted = birth.toLocaleDateString(locale, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    });
    return age >= 0 ? `${formatted} (${t("age", { count: age })})` : formatted;
};

const getDoctorNames = (patient) => {
    const names = Array.isArray(patient?.doc_names?.value)
        ? patient.doc_names.value
              .map((item) => (typeof item === "string" ? item : item?.name))
              .filter(Boolean)
        : [];
    if (names.length > 0) return names;

    const doc = patient?.doc_name?.value;
    if (!doc) return [];
    const single = typeof doc === "string" ? doc : doc.name;
    return single ? [single] : [];
};

const asArray = (value) => (Array.isArray(value) ? value : []);

/* -------------------------------------------------------------------------- */
/*  Карточка метрики                                                          */
/* -------------------------------------------------------------------------- */

function MetricCard({ icon, label, value, hint, loading }) {
    return (
        <div className="pov-stat">
            <span className="pov-stat__icon" aria-hidden="true">
                {icon}
            </span>
            <span className="pov-stat__label">{label}</span>
            <span className="pov-stat__value">
                {loading ? "—" : value}
            </span>
            {hint ? <span className="pov-stat__hint">{hint}</span> : null}
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  Обзор карточки пациента                                                   */
/* -------------------------------------------------------------------------- */

export default function PatientOverview() {
    const { t, i18n } = useTranslation("patient");
    const locale = getLocale(i18n.language);
    const { patientId } = useParams();
    const outlet = useOutletContext() || {};
    const {
        patient,
        patientVersion = 0,
        onEditBrief,
        onOpenAi,
    } = outlet;

    const [stats, setStats] = useState({
        appointments: 0,
        analyses: 0,
        plans: 0,
        paid: 0,
        planTotal: 0,
    });
    const [loading, setLoading] = useState(true);
    const [showPayments, setShowPayments] = useState(false);
    const [showHistory, setShowHistory] = useState(false);

    const loadStats = useCallback(async () => {
        setLoading(true);
        try {
            const [analysesRes, paymentsRes, plansRes] = await Promise.all([
                GetData({ module: "patient", block: "analysis", id_patient: patientId }),
                GetData({ module: "patient", block: "payments_list", id_patient: patientId }),
                GetData({ module: "patient", block: "treatment_plan_list", id_patient: patientId }),
            ]);

            const analyses = asArray(analysesRes?.result);
            const payments = asArray(paymentsRes?.result);
            const plans = asArray(plansRes?.plans || plansRes?.result);

            const paid = payments.reduce(
                (acc, item) => acc + (Number(item?.amount) || 0),
                0,
            );
            const planTotal = plans.reduce(
                (acc, plan) => acc + (Number(plan?.total_calculated_price) || 0),
                0,
            );
            const appointments = plans.reduce(
                (acc, plan) => acc + (Number(plan?.count_appointment) || 0),
                0,
            );

            setStats({
                appointments,
                analyses: analyses.length,
                plans: plans.length,
                paid,
                planTotal,
            });
        } finally {
            setLoading(false);
        }
    }, [patientId]);

    useEffect(() => {
        loadStats();
    }, [loadStats, patientVersion]);

    const doctors = useMemo(() => getDoctorNames(patient), [patient]);

    const money = useCallback(
        (value) =>
            `${Number(value || 0).toLocaleString(locale, {
                maximumFractionDigits: 0,
            })} ${t("overview.currency")}`,
        [locale, t],
    );

    const quickActions = [
        {
            to: `/patient/${patientId}/profile`,
            icon: <IoDocumentTextOutline />,
            title: t("overview.quickMedcard"),
            sub: t("overview.quickMedcardSub"),
        },
        {
            to: `/patient/${patientId}/diagnostics`,
            icon: <IoFlaskOutline />,
            title: t("overview.quickDiagnostics"),
            sub: t("overview.quickDiagnosticsSub"),
        },
        {
            to: `/patient/${patientId}/plan`,
            icon: <IoCalendarOutline />,
            title: t("overview.quickPlan"),
            sub: t("overview.quickPlanSub"),
        },
        {
            to: `/patient/${patientId}/documents`,
            icon: <IoGridOutline />,
            title: t("overview.quickDocuments"),
            sub: t("overview.quickDocumentsSub"),
        },
    ];

    const contacts = [
        {
            label: t("layout.brief.phone"),
            value: displayValue(patient?.phone?.value),
        },
        {
            label: t("layout.brief.birthDate"),
            value: formatBirthDate(patient?.birthDate?.value, locale, t),
        },
        {
            label: t("layout.brief.gender"),
            value: displayGender(patient?.gender?.value, t),
        },
        {
            label: t("layout.brief.clinic"),
            value: displayValue(patient?.clinic_name?.value),
        },
        {
            label: t("layout.brief.visitType"),
            value: displayValue(patient?.type_label?.value),
        },
        {
            label: t("layout.brief.email"),
            value: displayValue(patient?.mail?.value),
        },
        {
            label: t("layout.brief.cart"),
            value: displayValue(patient?.cart?.value),
        },
        {
            label: t("layout.brief.idYc"),
            value: `${displayValue(patientId)} / ${displayValue(
                patient?.id_yc?.value,
            )}`,
        },
    ];

    return (
        <div className="pov">
            {/* Быстрые переходы по разделам карточки */}
            <div className="pov-quick">
                {quickActions.map((action) => (
                    <Link
                        key={action.to}
                        to={action.to}
                        className="pov-quick__btn">
                        <span className="pov-quick__ic">{action.icon}</span>
                        <span className="pov-quick__text">
                            <span className="pov-quick__t">{action.title}</span>
                            <span className="pov-quick__s">{action.sub}</span>
                        </span>
                    </Link>
                ))}
            </div>

            {/* Ключевые метрики пациента */}
            <div className="pov-metrics">
                <MetricCard
                    icon={<IoTimeOutline />}
                    label={t("overview.metricAppointments")}
                    value={stats.appointments}
                    hint={t("overview.metricAppointmentsHint")}
                    loading={loading}
                />
                <MetricCard
                    icon={<IoFlaskOutline />}
                    label={t("overview.metricAnalyses")}
                    value={stats.analyses}
                    hint={t("overview.metricAnalysesHint")}
                    loading={loading}
                />
                <MetricCard
                    icon={<IoPulseOutline />}
                    label={t("overview.metricPlans")}
                    value={stats.plans}
                    hint={t("overview.metricPlansHint")}
                    loading={loading}
                />
                <MetricCard
                    icon={<IoCashOutline />}
                    label={t("overview.metricPaid")}
                    value={money(stats.paid)}
                    hint={t("overview.metricPaidHint")}
                    loading={loading}
                />
            </div>

            <div className="pov-grid pov-grid--2-1">
                {/* Контакты пациента */}
                <div className="pov-card">
                    <div className="pov-card__head">
                        <div>
                            <div className="pov-card__title">
                                {t("overview.contacts")}
                            </div>
                            <div className="pov-card__sub">
                                {doctors.length > 0
                                    ? `${t("layout.brief.doctor")}: ${doctors.join(
                                          ", ",
                                      )}`
                                    : t("overview.contactsSub")}
                            </div>
                        </div>
                        {onEditBrief ? (
                            <Button
                                icon={<IoPencilOutline />}
                                onClick={onEditBrief}
                                size="sm"
                                variant="soft">
                                {t("layout.edit")}
                            </Button>
                        ) : null}
                    </div>
                    <div className="pov-contacts">
                        {contacts.map((item) => (
                            <div className="pov-contacts__item" key={item.label}>
                                <span className="pov-contacts__label">
                                    {item.label}
                                </span>
                                <span className="pov-contacts__value">
                                    {item.value}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* ИИ-консультант: вся карточка открывает стандартную панель
                    ИИ-помощника (кнопка внутри — тот же обработчик). */}
                <div
                    className="pov-ai"
                    role="button"
                    tabIndex={0}
                    onClick={() => onOpenAi?.()}
                    onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            onOpenAi?.();
                        }
                    }}>
                    <div className="pov-ai__visual" aria-hidden="true">
                        <PatientAiOrb />
                    </div>
                    <div className="pov-ai__title">
                        {t("layout.aiConsultant")}
                    </div>
                    <div className="pov-ai__sub">{t("overview.aiSub")}</div>
                    <Button
                        icon={<IoSparklesOutline />}
                        onClick={(event) => {
                            event.stopPropagation();
                            onOpenAi?.();
                        }}
                        variant="accent">
                        {t("overview.aiOpen")}
                    </Button>
                </div>
            </div>

            <div className="pov-grid pov-grid--stack">
                {/* История изменений */}
                <div className="pov-card">
                    <div className="pov-card__head">
                        <div>
                            <div className="pov-card__title">
                                {t("layout.history")}
                            </div>
                            <div className="pov-card__sub">
                                {t("overview.historySub")}
                            </div>
                        </div>
                        <Button
                            icon={<IoArrowForwardOutline />}
                            onClick={() => setShowHistory(true)}
                            size="sm"
                            variant="soft">
                            {t("overview.openAll")}
                        </Button>
                    </div>
                    <div className="pov-card__body">
                        <PatientHistory embedded limit={3} />
                    </div>
                </div>

                {/* Финансы пациента */}
                <div className="pov-card">
                    <div className="pov-card__head">
                        <div>
                            <div className="pov-card__title">
                                {t("layout.finance")}
                            </div>
                            <div className="pov-card__sub">
                                {loading
                                    ? t("overview.financeSub")
                                    : `${t("overview.metricPaid")}: ${money(
                                          stats.paid,
                                      )}`}
                            </div>
                        </div>
                        <Button
                            icon={<IoArrowForwardOutline />}
                            onClick={() => setShowPayments(true)}
                            size="sm"
                            variant="soft">
                            {t("overview.openAll")}
                        </Button>
                    </div>
                    <div className="pov-card__body">
                        <PatientPayments limit={3} />
                    </div>
                </div>
            </div>

            <Modal
                show={showPayments}
                onHide={() => setShowPayments(false)}
                size="xl"
                centered
                className="patient-history-modal">
                <Modal.Header closeButton>
                    <Modal.Title>{t("layout.finance")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {showPayments ? <PatientPayments /> : null}
                </Modal.Body>
            </Modal>

            <Modal
                show={showHistory}
                onHide={() => setShowHistory(false)}
                size="xl"
                centered
                className="patient-history-modal">
                <Modal.Header closeButton>
                    <Modal.Title>{t("layout.history")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {showHistory ? <PatientHistory embedded /> : null}
                </Modal.Body>
            </Modal>
        </div>
    );
}
